import { useTranslations } from "next-intl";
import { useState } from "react";

export const useDatePicker = (
  initialDate: Date,
  onSelectDate: (date: Date) => void,
  closeDatePicker: () => void,
  /**
   * Whether picking a day immediately commits + closes. True for date-only
   * fields (there's nothing else to set). False for date+time fields, where a
   * day click only SELECTS the day and the user confirms via the TimePicker's
   * save button after also setting the time — otherwise clicking a day would
   * apply with whatever time happened to be there and close before the user
   * could set the clock.
   */
  commitOnDaySelect: boolean = true
) => {
  const t = useTranslations("datePicker");
  const [currentDate, setCurrentDate] = useState(initialDate);

  // Generate calendar data using UTC methods
  const generateCalendarDays = () => {
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth();

    // First day of the month in UTC
    const firstDay = new Date(Date.UTC(year, month, 1));
    // Last day of the month in UTC
    const lastDay = new Date(Date.UTC(year, month + 1, 0));

    const daysInMonth = lastDay.getUTCDate();
    const startDayOfWeek =
      firstDay.getUTCDay() === 0 ? 6 : firstDay.getUTCDay() - 1; // Adjust for Monday start

    // Previous month days that appear in this month's calendar
    const prevMonthDays = [];
    if (startDayOfWeek > 0) {
      const prevMonthLastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
      // Calculate the correct year for previous month
      const prevMonth = month - 1;
      const prevYear = prevMonth < 0 ? year - 1 : year;
      const prevMonthIndex = prevMonth < 0 ? 11 : prevMonth;

      for (
        let i = prevMonthLastDay - startDayOfWeek + 1;
        i <= prevMonthLastDay;
        i++
      ) {
        prevMonthDays.push({
          day: i,
          month: prevMonthIndex,
          year: prevYear,
          isCurrentMonth: false,
        });
      }
    }

    // Current month days
    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
      });
    }

    // Next month days that appear in this month's calendar
    const nextMonthDays = [];
    const totalDaysDisplayed = prevMonthDays?.length + currentMonthDays?.length;
    const daysToAdd = 42 - totalDaysDisplayed; // 6 rows of 7 days

    // Calculate the correct year for next month
    const nextMonth = month + 1;
    const nextYear = nextMonth > 11 ? year + 1 : year;
    const nextMonthIndex = nextMonth > 11 ? 0 : nextMonth;

    for (let i = 1; i <= daysToAdd; i++) {
      nextMonthDays.push({
        day: i,
        month: nextMonthIndex,
        year: nextYear,
        isCurrentMonth: false,
      });
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const calendarDays = generateCalendarDays();

  // Get month names
  const months = [
    t("january"),
    t("february"),
    t("march"),
    t("april"),
    t("may"),
    t("june"),
    t("july"),
    t("august"),
    t("september"),
    t("october"),
    t("november"),
    t("december"),
  ];

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(
        Date.UTC(
          currentDate.getUTCFullYear(),
          currentDate.getUTCMonth() - 1,
          1,
          currentDate.getUTCHours(),
          currentDate.getUTCMinutes()
        )
      )
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(
        Date.UTC(
          currentDate.getUTCFullYear(),
          currentDate.getUTCMonth() + 1,
          1,
          currentDate.getUTCHours(),
          currentDate.getUTCMinutes()
        )
      )
    );
  };

  const handleSelectDay = (day: number, month: number, year: number) => {
    const newDate = new Date(
      Date.UTC(
        year,
        month,
        day,
        currentDate.getUTCHours(),
        currentDate.getUTCMinutes()
      )
    );
    setCurrentDate(newDate);
    // Date+time fields: only select the day; the user still sets the time and
    // commits via the save button. Date-only fields commit right away.
    if (commitOnDaySelect) {
      onSelectDate(newDate);
      closeDatePicker();
    }
  };

  // Day names
  const dayNames = [
    t("monday"),
    t("tuesday"),
    t("wednesday"),
    t("thursday"),
    t("friday"),
    t("saturday"),
    t("sunday"),
  ];

  return {
    currentDate,
    calendarDays,
    months,
    dayNames,
    handlePrevMonth,
    handleNextMonth,
    handleSelectDay,
    setCurrentDate,
  };
};
